import { Component, inject, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface CapModalData {
  planName: string;
  ordersIncluded: number;
  overagePrice: number;
  currentCap: number;
  maxCap: number;
  isBetaUser?: boolean;
  isCreatingAccount?: boolean;
}

@Component({
  selector: 'app-cap-modal',
  imports: [DecimalPipe, FormsModule],
  styleUrl: './cap-modal.component.scss',
  template: `
    <div class="modal-header">
      <h5 class="modal-title">Customize Monthly Maximum</h5>
      <button
        type="button"
        class="btn-close"
        aria-label="Close"
        (click)="activeModal.dismiss()"
      ></button>
    </div>
    <div class="modal-body">
      <p class="text-muted">
        Set your Monthly Maximum for the {{ data.planName }} plan. Flex Orders cost \${{
          data.overagePrice
        }}
        each.
      </p>

      @if (data.isBetaUser && data.isCreatingAccount) {
        <div class="alert alert-success mb-3">
          <i class="bi bi-check-circle-fill me-2"></i>
          <strong>Beta discount active:</strong>
          Your cap will be 50% off for the first year!
        </div>
      }

      <div class="mb-3">
        <label class="form-label">Cap Amount ($)</label>
        <input
          #rangeInput
          type="range"
          class="form-range"
          [min]="0"
          [max]="data.maxCap"
          [(ngModel)]="capValue"
          (input)="updateCap()"
        />
        <div class="d-flex justify-content-between">
          <small class="text-muted">$0</small>
          @if (data.isBetaUser && data.isCreatingAccount) {
            <strong>
              <span class="text-muted small" style="text-decoration: line-through;">
                \${{ capValue }}
              </span>
              <span class="text-success ms-1">\${{ getDiscountedCap() }}</span>
            </strong>
          } @else {
            <strong>\${{ capValue }}</strong>
          }
          <small class="text-muted">\${{ data.maxCap }}</small>
        </div>
      </div>

      <div>
        <div class="d-flex justify-content-between mb-2">
          <span>Base orders included:</span>
          <strong>{{ data.ordersIncluded | number }}</strong>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <span>Additional orders (with Cap):</span>
          <strong>{{ additionalOrders | number: '1.0-0' }}</strong>
        </div>
        <hr />
        <div class="d-flex justify-content-between">
          <span>Max orders per month:</span>
          <strong>{{ maxOrders | number }}</strong>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="activeModal.dismiss()">
        Cancel
      </button>
      <button type="button" class="btn btn-primary" (click)="save()">Save Cap Settings</button>
    </div>
  `
})
export class CapModalComponent implements AfterViewInit {
  activeModal = inject(NgbActiveModal);

  @ViewChild('rangeInput') rangeInput!: ElementRef<HTMLInputElement>;

  data!: CapModalData;
  capValue = 0;
  additionalOrders = 0;
  maxOrders = 0;

  ngOnInit() {
    this.capValue = this.data.currentCap;
    this.updateCap();
  }

  ngAfterViewInit() {
    this.updateRangeProgress();
  }

  getDiscountedCap(): number {
    return this.capValue * 0.5;
  }

  updateCap() {
    // For beta users: they pay discounted price but get orders based on ORIGINAL cap amount
    // Example: Pay $25 (50% off), but get 4000 orders (as if they paid $50)
    this.additionalOrders = Math.floor(this.capValue / this.data.overagePrice);
    this.maxOrders = this.data.ordersIncluded + this.additionalOrders;
    this.updateRangeProgress();
  }

  updateRangeProgress() {
    if (this.rangeInput) {
      const progress = (this.capValue / this.data.maxCap) * 100;
      this.rangeInput.nativeElement.style.setProperty('--range-progress', `${progress}%`);
    }
  }

  save() {
    this.activeModal.close(this.capValue);
  }
}
